(function () {
  const STORAGE_KEYS = {
    cid: 'po_url_promo_cid',
    promocode: 'po_url_promo_promocode',
    dismissed: 'po_url_promo_dismissed',
  };
  const COOKIE_KEYS = {
    cid: 'po_url_promo_cid',
    promocode: 'po_url_promo_promocode',
  };
  const COOKIE_DURATION_DAYS = 30;
  const DEFAULT_CART_BANNER_TEXT =
    'Use code "[code]" to enjoy $10 off. Click "Apply promo code" to automatically apply the discount.';
  const DEFAULT_CART_BANNER_APPLY_LABEL = 'Apply promo code';
  const DEFAULT_CART_PROMO_BOX_TITLE = 'You have 1 promo code to use';
  const DEFAULT_CART_PROMO_DROPDOWN_TITLE = 'Do you have a promo code?';
  const DEFAULT_CART_BANNER_SUCCESS_TEXT = 'Your promotion has been applied';

  const normalizeValue = (value) => String(value || '').trim();
  const normalizeCode = (value) => normalizeValue(value).toLowerCase();

  const setSessionValue = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // no-op: sessionStorage can be unavailable
    }
  };

  const getSessionValue = (key) => {
    try {
      return normalizeValue(window.sessionStorage.getItem(key));
    } catch (error) {
      return '';
    }
  };

  const setCookieValue = (key, value, days) => {
    const expiresAt = new Date();
    expiresAt.setTime(expiresAt.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; expires=${expiresAt.toUTCString()}; path=/; SameSite=Lax`;
  };

  const getCookieValue = (key) => {
    const needle = `${encodeURIComponent(key)}=`;
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(needle)) {
        return normalizeValue(decodeURIComponent(cookie.slice(needle.length)));
      }
    }

    return '';
  };

  const persistPromoData = (cidValue, promoValue) => {
    setSessionValue(STORAGE_KEYS.cid, cidValue);
    setSessionValue(STORAGE_KEYS.promocode, promoValue);
    setCookieValue(COOKIE_KEYS.cid, cidValue, COOKIE_DURATION_DAYS);
    setCookieValue(COOKIE_KEYS.promocode, promoValue, COOKIE_DURATION_DAYS);
  };

  const resolvePromoData = () => {
    const params = new URLSearchParams(window.location.search);
    let cid = normalizeValue(params.get('cid'));
    let promocode = normalizeValue(params.get('promocode'));

    if (cid && promocode) {
      persistPromoData(cid, promocode);
    } else {
      cid = getSessionValue(STORAGE_KEYS.cid) || getCookieValue(COOKIE_KEYS.cid);
      promocode = getSessionValue(STORAGE_KEYS.promocode) || getCookieValue(COOKIE_KEYS.promocode);

      if (cid && promocode) {
        setSessionValue(STORAGE_KEYS.cid, cid);
        setSessionValue(STORAGE_KEYS.promocode, promocode);
      }
    }

    return { cid, promocode };
  };

  const isDismissedForCurrentSession = () => getSessionValue(STORAGE_KEYS.dismissed) === 'true';

  const getCartCopy = (popup) => ({
    messageTemplate:
      normalizeValue(popup?.dataset?.cartBannerText) || DEFAULT_CART_BANNER_TEXT,
    applyLabel:
      normalizeValue(popup?.dataset?.cartBannerApplyLabel) || DEFAULT_CART_BANNER_APPLY_LABEL,
    promoBoxTitle:
      normalizeValue(popup?.dataset?.cartPromoBoxTitle) || DEFAULT_CART_PROMO_BOX_TITLE,
    promoDropdownTitle:
      normalizeValue(popup?.dataset?.cartPromoDropdownTitle) || DEFAULT_CART_PROMO_DROPDOWN_TITLE,
    successText:
      normalizeValue(popup?.dataset?.cartBannerSuccessText) || DEFAULT_CART_BANNER_SUCCESS_TEXT,
    promoI18n: {
      placeholder: normalizeValue(popup?.dataset?.cartPromoInputPlaceholder),
      apply_code: normalizeValue(popup?.dataset?.cartPromoApplyCodeLabel),
      searching: normalizeValue(popup?.dataset?.cartPromoSearchingText),
      applied_code:
        normalizeValue(popup?.dataset?.cartBannerSuccessText) || DEFAULT_CART_BANNER_SUCCESS_TEXT,
      invalid_code: normalizeValue(popup?.dataset?.cartPromoInvalidCodeText),
      not_applied_reason: normalizeValue(popup?.dataset?.cartPromoNotAppliedReasonText),
      not_applied_message: normalizeValue(popup?.dataset?.cartPromoNotAppliedMessage),
      not_combinable_message: normalizeValue(popup?.dataset?.cartPromoNotCombinableMessage),
    },
  });

  const formatCartMessage = (template, promocode) =>
    normalizeValue(template || DEFAULT_CART_BANNER_TEXT).replace(/\[code\]/g, promocode);

  const getCurrentAppliedCodes = () =>
    Array.from(document.querySelectorAll('[data-po-cart-promo-codes] [data-discountcode]'))
      .map((node) => normalizeValue(node.dataset.discountcode || node.textContent))
      .filter(Boolean);

  const hasAppliedPromoCode = (promocode, appliedCodes) => {
    const normalizedPromoCode = normalizeCode(promocode);
    return appliedCodes.some((code) => normalizeCode(code) === normalizedPromoCode);
  };

  const setIconState = (banner, applied) => {
    const availableIcon = banner.querySelector('[data-url-promo-banner-icon="available"]');
    const successIcon = banner.querySelector('[data-url-promo-banner-icon="success"]');

    if (availableIcon) availableIcon.hidden = applied;
    if (successIcon) successIcon.hidden = !applied;
  };

  const renderCartBanners = (banners, popup, promoData, appliedCodes = getCurrentAppliedCodes()) => {
    if (!banners.length || !promoData.promocode) return;

    const copy = getCartCopy(popup);
    const applied = hasAppliedPromoCode(promoData.promocode, appliedCodes);

    banners.forEach((banner) => {
      const variant = banner.dataset.urlPromoBannerVariant || 'top';

      if (applied && variant === 'promo-panel') {
        banner.hidden = true;
        return;
      }

      const title = banner.querySelector('[data-url-promo-banner-title]');
      const message = banner.querySelector('[data-url-promo-banner-message]');
      const applyButton = banner.querySelector('[data-url-promo-apply]');
      const applyLabel = banner.querySelector('[data-url-promo-apply-label]');
      const successText =
        copy.successText || normalizeValue(banner.dataset.urlPromoSuccessText) || copy.messageTemplate;

      if (title) {
        title.textContent = copy.promoBoxTitle;
      }

      if (message) {
        message.textContent = applied
          ? successText
          : formatCartMessage(copy.messageTemplate, promoData.promocode);
      }

      if (applyLabel) {
        applyLabel.textContent = copy.applyLabel;
      }

      if (applyButton) {
        applyButton.hidden = applied;
      }

      setIconState(banner, applied);
      banner.dataset.urlPromoApplied = applied ? 'true' : 'false';
      banner.hidden = false;
    });
  };

  const bindCartBannerApply = (banner, promoData) => {
    if (banner.dataset.urlPromoBannerBound === 'true') return;
    banner.dataset.urlPromoBannerBound = 'true';

    banner.querySelector('[data-url-promo-apply]')?.addEventListener('click', (event) => {
      event.preventDefault();
      document.dispatchEvent(
        new CustomEvent('po:url-promo:apply', {
          detail: {
            cid: promoData.cid,
            promocode: promoData.promocode,
          },
        })
      );
    });
  };

  const updateCartPromoDropdownTitle = (copy) => {
    document.querySelectorAll('[data-po-cart-promo-title]').forEach((title) => {
      title.textContent = copy.promoDropdownTitle;
    });
  };

  const updateCartPromoI18n = (copy) => {
    window.__poDiscountI18n = window.__poDiscountI18n || {};

    Object.entries(copy.promoI18n).forEach(([key, value]) => {
      if (value) {
        window.__poDiscountI18n[key] = value;
      }
    });

    window.poDiscountDropdownTranslate?.();
  };

  const initCartBanners = (popup, promoData, copy = getCartCopy(popup)) => {
    const banners = Array.from(document.querySelectorAll('[data-url-promo-banner]'));
    if (!banners.length) return;

    banners.forEach((banner) => bindCartBannerApply(banner, promoData));
    renderCartBanners(banners, popup, promoData);

    document.addEventListener('po:cart-promo-code:state', (event) => {
      const appliedCodes = Array.isArray(event.detail?.codes) ? event.detail.codes : getCurrentAppliedCodes();
      renderCartBanners(banners, popup, promoData, appliedCodes);
    });

    document.addEventListener('po:cart:discount-updated', () => {
      renderCartBanners(banners, popup, promoData);
    });
  };

  const initPopup = (popup, promoData) => {
    if (!popup || isDismissedForCurrentSession()) return;

    const codeElement = popup.querySelector('[data-url-promo-code]');
    const copyButton = popup.querySelector('[data-url-promo-copy]');
    const closeButton = popup.querySelector('[data-url-promo-popup-close]');

    if (!codeElement || !copyButton || !closeButton) return;

    const defaultCopyLabel = popup.dataset.copyLabel || 'COPY';
    const copiedLabel = popup.dataset.copiedLabel || 'COPIED';

    codeElement.textContent = promoData.promocode;
    popup.hidden = false;

    closeButton.addEventListener('click', () => {
      setSessionValue(STORAGE_KEYS.dismissed, 'true');
      popup.hidden = true;
    });

    copyButton.addEventListener('click', async () => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(promoData.promocode);
        } else {
          const helper = document.createElement('textarea');
          helper.value = promoData.promocode;
          helper.setAttribute('readonly', '');
          helper.style.position = 'absolute';
          helper.style.left = '-9999px';
          document.body.appendChild(helper);
          helper.select();
          document.execCommand('copy');
          helper.remove();
        }

        copyButton.textContent = copiedLabel;
        window.setTimeout(() => {
          copyButton.textContent = defaultCopyLabel;
        }, 1500);
      } catch (error) {
        copyButton.textContent = defaultCopyLabel;
      }
    });
  };

  const init = () => {
    const popup = document.getElementById('po-url-promo-popup');
    const hasCartBanners = document.querySelectorAll('[data-url-promo-banner]').length > 0;

    if (!popup && !hasCartBanners) return;

    const copy = getCartCopy(popup);
    updateCartPromoDropdownTitle(copy);
    updateCartPromoI18n(copy);

    const promoData = resolvePromoData();
    if (!promoData.cid || !promoData.promocode) return;

    initPopup(popup, promoData);
    initCartBanners(popup, promoData, copy);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
